import { AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { CustomerDto } from 'src/app/shared/dto/customer.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { CustomersService } from '../../services/customers.service';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { DRAWER_WIDTH_CHANGE_PLACEMENT } from 'src/app/core/constants/components';
import { NzFormDirective, NzFormItemComponent, NzFormControlComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzRowDirective, NzColDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { DrawerMode } from 'src/app/core/enums/drawer-mode';
import { IdentityDocumentTypesService } from '../../services/identity-document-types.service';
import { IdentityDocumentTypeDto } from 'src/app/shared/dto/identity-document-type.dto';
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
    selector: 'app-customers',
    standalone: true,
    imports: [
        NzPageHeaderModule,
        NzSpaceModule,
        NzDividerModule,
        NzDrawerModule,
        NzDatePickerModule,
        FormsModule,
        NzMessageModule,
        NzTableModule,
        NzSelectModule,
        NzInputDirective,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzFormModule,
        NzIconModule,
        NzToolTipModule,
        NzDropDownModule,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './customers.component.html',
    styleUrl: './customers.component.scss',
})
export class CustomersComponent extends DefaultComponent implements OnInit, AfterViewInit, OnDestroy {

    fieldsReadOnly: boolean = true;
    drawerTitle: string = 'Placeholder';
    drawerMode!: DrawerMode;
    drawerVisible: boolean = false;
    customerSelected: CustomerDto = new CustomerDto();
    customers: CustomerDto[] = [];
    identityDocumentTypes: IdentityDocumentTypeDto[] = [];
    identityDocumentTypeSelected: IdentityDocumentTypeDto = new IdentityDocumentTypeDto();
    currentUser: UserDto = new UserDto();
    isLoading: boolean = true;
    isSaving: boolean = false;
    
    innerWidth: number = 0;
    DRAWER_WIDTH_CHANGE_PLACEMENT = DRAWER_WIDTH_CHANGE_PLACEMENT;

    validateForm: FormGroup<{
        name: FormControl<string>;
        externalId: FormControl<number | null>;
        identityDocument: FormControl<string>;
        identityDocumentId: FormControl<number>;
        businessName: FormControl<string>;
    }>;

    readonly customerService = inject(CustomersService);
    readonly identityDocumentTypesService = inject(IdentityDocumentTypesService);
    readonly message = inject(NzMessageService);
    readonly userLoggedService = inject(UserLoggedService);
    readonly fb = inject(NonNullableFormBuilder);

    private unsubscribe$ = new Subject<void>();

    constructor() {
        super('customers', '');
        this.validateForm = this.fb.group({
            name: ['', [Validators.required]],
            externalId: new FormControl<number | null>(null),
            identityDocument: ['', [Validators.required]],
            identityDocumentId: [1, [Validators.required]],
            businessName: ['', [Validators.required]]
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.innerWidth = window.innerWidth;
    }

    async ngOnInit() {
        this.currentUser = this.userLoggedService.userLogged;
        this.innerWidth = window.innerWidth;

        await this.customerService.setupWebSocket();
        this.customerService.customers$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (customers) => {
                    this.customers = customers;
                    this.customerSelected = this.customers.find(customer => customer.id === this.customerSelected.id) || new CustomerDto();
                },
                error: (e) => {
                    this.message.create('Error cargando clientes', e.message);
                },
            });

        await this.identityDocumentTypesService.setupWebSocket();
        this.identityDocumentTypesService.identityDocumentTypes$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (identityDocumentTypes) => {
                    this.identityDocumentTypes = identityDocumentTypes;
                },
                error: () => {
                    this.message.error('Error cargando tipos de documentos de identidad');
                }
            });

        this.isLoading = false;
    }

    ngAfterViewInit() {
        this.innerWidth = window.innerWidth;
    }

    ngOnDestroy(): void {
        this.customerService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    create() {
        this.customerSelected = new CustomerDto();
        this.drawerMode = DrawerMode.CREATE;
        this.drawerTitle = 'Creando nuevo cliente';
        this.fieldsReadOnly = false;
        this.validateForm.enable();

        this.openDrawer();
    }

    view(data: CustomerDto) {
        this.validateForm.patchValue(data);
        this.validateForm.disable();
        this.customerSelected = data;
        this.drawerMode = DrawerMode.VIEW;
        this.drawerTitle = 'Viendo a cliente ' + this.customerSelected?.name;
        this.fieldsReadOnly = true;

        this.setData();
        this.openDrawer();
    }

    edit(data: CustomerDto) {
        this.customerSelected = data;
        this.validateForm.patchValue(data);
        this.validateForm.enable();
        this.drawerMode = DrawerMode.EDIT;
        this.drawerTitle = 'Editando a cliente ' + this.customerSelected?.name;
        this.fieldsReadOnly = false;

        this.setData();
        this.openDrawer();
    }

    async delete(customerId: number) {
        try {
            if (!window.confirm('¿Estás seguro de que quieres eliminar este cliente? Esta acción no se puede deshacer.')) {
                return;
            }

            const deletedCustomer: CustomerDto = await this.customerService.deleteCustomer(customerId);

            const index = this.customers.findIndex((c) => c.id === customerId);

            if (index < 0) {
                this.message.create('error', `No se ha podido eliminar el cliente ${deletedCustomer.name} con id ${customerId}, cliente no encontrado`);
                return;
            }

            this.customers.splice(index, 1);

            const data = [...this.customers];
            this.customers = data;
            this.message.create('success', 'Cliente eliminado correctamente');
        } catch (e: any) {
            this.message.create('error', `${e.message}`);
        }
    }

    setData() {

    }

    openDrawer() {
        this.drawerVisible = true;
    }

    closeDrawer() {
        this.customerSelected = new CustomerDto();
        this.validateForm.reset();
        this.drawerVisible = false;
    }

    async submitForm() {
        try {
            this.isSaving = true;

            const customerToBeProcessed: CustomerDto = {
                id: this.customerSelected.id,
                identityDocumentId: this.validateForm.controls.identityDocumentId.value,
                identityDocument: this.validateForm.controls.identityDocument.value
                    ? this.validateForm.controls.identityDocument.value
                    : '',
                name: this.validateForm.controls.name.value
                    ? this.validateForm.controls.name.value
                    : '',
                businessName: this.validateForm.controls.businessName.value
                    ? this.validateForm.controls.businessName.value
                    : '',
                createdBy: this.drawerMode === DrawerMode.CREATE ? this.currentUser.id : this.customerSelected.createdBy
            };

            if ((<HTMLInputElement>(document.getElementById('externalId'))).value) {
                customerToBeProcessed.externalId = +(<HTMLInputElement>(document.getElementById('externalId'))).value;
            }

            if (!this.isFormValid(customerToBeProcessed)) {
                this.isSaving = false;
                return;
            }

            if (this.drawerMode === DrawerMode.CREATE) {
                const createdCustomer: CustomerDto = await this.customerService.createCustomer(customerToBeProcessed);

                this.customers.push(createdCustomer);
                const data = [...this.customers];
                this.customers = data;

                this.isSaving = false;
                this.message.create('success', 'Cliente creado correctamente');
            } else if (this.drawerMode === DrawerMode.EDIT) {
                const updatedCustomer: CustomerDto = await this.customerService.updateCustomer(customerToBeProcessed);

                const index = this.customers.findIndex((c) => c.id === updatedCustomer.id);

                if (index < 0) {
                    this.isSaving = false;
                    this.message.create('error', `No se ha podido actualizar el cliente ${customerToBeProcessed.name} con id ${customerToBeProcessed.id}, cliente no encontrado`);

                    return;
                }

                this.customers[index] = updatedCustomer;
                const data = [...this.customers];
                this.customers = data;

                this.isSaving = false;
                this.message.create('success', 'Cliente actualizado correctamente');
            }

            this.closeDrawer();
        } catch (e: any) {
            this.isSaving = false;
            this.message.create('error', `Error: ${e.message}`);
        }
    }

    cancelEditing() {
        this.closeDrawer();
    }

    isFormValid(customer: CustomerDto): boolean {
        if (customer.name.length === 0) {
            this.message.create('error', 'El nombre no puede estar vacío');
            return false;
        }

        if (customer.identityDocument.length === 0) {
            this.message.create('error', 'El documento de identidad no puede estar vacío');
            return false;
        }

        if (customer.identityDocumentId === -1) {
            this.message.create('error', 'El id de documento de identidad no puede estar vacío');
            return false;
        }

        if (customer.businessName.length === 0) {
            this.message.create('error', 'El nombre de la empresa no puede estar vacío');
            return false;
        }

        return true;
    }

    compareIdentityDocumentTypes = (o1: string, o2: string): boolean => {
        return o1 === o2;
    };
}
